<template>
  <div>
    <b-modal :visible="showEditModal" :hide-footer="true" title="Edit Role" @hidden="onCloseRoleModal">
      <div class="alert alert-danger" v-if="error">
        {{ error }}
      </div>

      <div class="form-group">
        <label for="role_title">
          {{ titleFieldLabel }}
        </label>
        <input type="text" id="role_title" class="form-control" v-model="role.title">
      </div>

      <div class="form-group">
        <label for="role_reports_to">
          {{ reportsToFieldLabel }}
        </label>
        <select id="role_reports_to" class="form-control" v-model="role.reports_to">
          <option value="0">None</option>
          <option v-for="parent in chartData.roles" :key="parent.id" :value="parent.id">
            {{ parent.title }}
          </option>
        </select>
      </div>

      <div class="form-group" v-if="role.type === 'emp'">
        <label for="user_id">User</label>
        <autocomplete
          id="user_id"
          name="user_id"
          v-model="role.user"
          :url="usersSearchUrl"
          :prepare="function(query) { return { search: query, scope: 'App\\User' } }"
          :parse="function(result) { return { icon: 'fa-user', name: result.name || result.label } }"
        ></autocomplete>
      </div>

      <div class="form-group">
        <label for="role_description">
          Description
          <span class="ml-2 text-muted">
              (Optional)
            </span>
        </label>
        <textarea id="role_description" class="form-control" v-model="role.description"></textarea>
      </div>

      <hr>

      <div class="form-group">
        <label for="role_type">Chart Element Type</label>
        <small class="d-block mb-3">
          Need to create a subgroup of employees? Just change
          the type to <b>Group/Department</b>.
        </small>
        <select id="role_type" class="form-control" v-model="role.type">
          <option value="emp">Employee</option>
          <option value="grp">Group/Department</option>
          <option value="ven">Vendor/Partner</option>
          <option value="grp">System/Software</option>
        </select>
      </div>

      <div class="form-group">
        <label for="role_draw_direction">Draw Direction</label>
        <select id="role_draw_direction" class="form-control" v-model="role.draw_direction">
          <option value="horizontal">Horizontal</option>
          <option value="vertical">Vertical</option>
        </select>
      </div>

      <button class="btn btn-block btn-primary" @click.prevent="onClickSaveRole">
        Save Role
      </button>

    </b-modal>
    <div class="mb-4 d-flex justify-content-between">
      <div>
        <button class="btn btn-secondary" @click.prevent="onClickZoomIn">
          <i class="fa fa-plus"></i>
        </button>
        <button class="btn btn-secondary" @click.prevent="onClickZoomOut">
          <i class="fa fa-minus"></i>
        </button>
      </div>
      <div>
        <button class="btn btn-secondary" @click.prevent="onClickExport">
          Export
        </button>
        <button class="btn btn-primary" @click.prevent="onClickAddRole" v-if="editable">
          Add Role
        </button>
      </div>
    </div>
    <div id="org-chart" style="width:100%;min-height:100px;"></div>
    <!--<pre v-html="debug"></pre>-->
    <hr>
    <div class="row">
      <div class="col-12 col-md-4 col-lg-3" v-for="role in chartData.roles">
        <div class="card mb-3">
          <div :class="{
            'card-header': true,
            'bg-dark text-white': role.type === 'grp',
            'bg-flat-alizarin text-white': role.type === 'ven'
          }">
            {{ role.title }}
          </div>
          <div class="card-body">
            <div v-if="role.user" class="">
              <a href="#" class="media text-body">
                <span class="media-left mr-2">
                  <span class="profile-img tiny" :style="`background-image: url('${role.user.image}');`">
                    <span class="profile-img-body"></span>
                  </span>
                </span>
                <span class="media-body small">
                  {{ role.user.name }}
                </span>
              </a>
              <hr>
            </div>
            <div class="small" v-html="role.description" />
          </div>
          <div class="card-footer text-right">
            <div v-if="editable">
              <button class="btn btn-sm btn-white" @click.prevent="editRole(role.id)">
                Edit
              </button>
              <button class="btn btn-sm btn-white" @click.prevent="deleteRole(role.id)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get, merge, map, find } from 'lodash'
import api from '@/api'
import alertify from 'alertify.js'
import Autocomplete from '@/components/Autocomplete'

/*
const MOCK_DATA = [
  {
    'id': '1',
    'text': 'Redwood',
    'img': '../common/img/avatar-1.png'
  },
  {
    'id': '2.3',
    'title': 'Owner, PPP',
    'text': 'Shawn Milletary',
    'img': '../common/img/avatar-4.png',
    'parent': '1',
    'dir': 'vertical'
  },
  {
    'id': '2.2',
    'title': 'Owner, Leads & Ancillaries',
    'text': 'Nick Pasquini',
    'img': '../common/img/avatar-2.png',
    'parent': '1',
    'dir': 'horizontal'
  },
  {
    'id': '2.1',
    'title': 'Owner, Recruiting',
    'text': 'Eddie Berenbaum',
    'img': '../common/img/avatar-2.png',
    'parent': '1',
    'dir': 'horizontal'
  },
  {
    'id': '3',
    'title': 'General Manager',
    'text': 'Aaron Collegeman',
    'img': '../common/img/avatar-4.png',
    'parent': '1'
  }
]
*/

export default {
  components: {
    Autocomplete
  },
  props: {
    editable: {
      type: Boolean,
      default: true
    },
    usersSearchUrl: {
      type: String,
      required: true
    },
    chart: {
      type: Object,
      default () {
        return {
          roles: []
        }
      }
    },
    root: {
      type: Object,
      default () {
        return {
          text: 'Org Chart'
        }
      }
    }
  },
  computed: {
    debug () {
      return JSON.stringify(this.allRoles, null, 2)
    },
    titleFieldLabel () {
      if (this.role.type === 'grp') {
        return 'Group/Department Name'
      } else if (this.role.type === 'ven') {
        return 'Partner/Vendor Name'
      } else if (this.role.type === 'sys') {
        return 'System/Software Name'
      } else {
        return 'Title/Role'
      }
    },
    reportsToFieldLabel () {
      if (this.role.type === 'grp') {
        return 'Directed By'
      } else if (this.role.type === 'ven') {
        return 'Company Liason'
      } else if (this.role.type === 'sys') {
        return 'Owned By'
      } else {
        return 'Reports To'
      }
    },
    allRoles () {
      const allRoles = [{
        id: 'root',
        text: get(this.root, 'text'),
        img: ''
      }].concat(map(this.chartData.roles, (role) => {
        return {
          id: `${role.id}`,
          parent: `${role.reports_to || 'root'}`,
          text: role.title,
          img: role.user ? role.user.image : '',
          title: role.user ? role.user.name : '',
          user: null,
          dir: role.draw_direction
        }
      }))

      return allRoles
    }
  },
  data () {
    const chartData = this.chart || { roles: [] }

    const defaultRole = {
      id: null,
      type: 'emp',
      title: null,
      description: null,
      reports_to: 0,
      draw_direction: 'horizontal'
    }

    return {
      chartData,
      defaultRole,
      error: null,
      showEditModal: false,
      saving: false,
      diagram: null,
      zoomLevel: 0.7,
      role: Object.assign({}, defaultRole)
    }
  },
  watch: {
    'chartData': {
      handler () {
        this.diagram.data.removeAll()
        for (let i = 0; i < this.allRoles.length; i++) {
          this.diagram.data.add(this.allRoles[i])
        }
        this.diagram.paint()
      },
      deep: true
    },
    zoomLevel () {
      this.diagram.config.scale = this.zoomLevel
      this.diagram.paint()
    }
  },
  mounted () {
    this.diagram = new window.dhx.Diagram('org-chart', {
      type: 'org',
      scale: this.zoomLevel,
      defaultShapeType: 'img-card'
    })

    this.diagram.data.parse(this.allRoles)

    this.diagram.events.on('ShapeClick', (id) => {
      this.editRole(parseInt(id))
    })
  },
  methods: {
    onClickAddRole () {
      this.showEditModal = true
      this.role = Object.assign({}, this.defaultRole)
    },
    onClickSaveRole () {
      this.saveRole()
    },
    onClickZoomOut () {
      this.zoomLevel = Math.max(0.1, this.zoomLevel - 0.1)
    },
    onClickZoomIn () {
      this.zoomLevel = Math.min(1.0, this.zoomLevel + 0.1)
    },
    onClickExport () {
      this.diagram.export.pdf()
    },
    onCloseRoleModal () {
      this.showEditModal = false
      this.role = Object.assign({}, this.defaultRole)
    },
    saveRole () {
      api[this.role.id ? 'put' : 'post'](`org-charts/${this.chart.id}/roles/${this.role.id || ''}`, merge(this.role, { org_chart_id: this.chart.id }))
        .then((response) => {
          this.onCloseRoleModal()
          this.saving = false
          this.chartData = response.data
        })
    },
    editRole (roleId) {
      const role = find(this.chartData.roles, { 'id': roleId })
      this.role = Object.assign({}, role)
      this.showEditModal = true
    },
    deleteRole (id) {
      alertify.confirm('Are you sure you want to delete this role?', () => {
        api.delete(`org-charts/${this.chart.id}/roles/${id}`)
          .then((response) => {
            this.chartData = response.data
          })
      })
    }
  }
}
</script>